import React, { useState, useEffect } from 'react';
import { Clock, User, FileText } from 'lucide-react';
import { useBusiness } from '../../../contexts/BusinessContext';
import api from '../../../services/api';

const ActivityLogTab = ({ staffId }) => {
  const { activeBusiness } = useBusiness();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (staffId && activeBusiness?.id) {
      fetchAuditLogs();
    } else {
      setLoading(false);
    }
  }, [staffId, activeBusiness]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/users/field-staff/${staffId}/audit`);
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeLabel = (type) => {
    const labels = {
      created: 'Created',
      updated: 'Updated',
      permissions_updated: 'Permissions Updated',
      status_changed: 'Status Changed',
      deactivated: 'Deactivated',
      password_reset: 'Password Reset'
    };
    return labels[type] || type;
  };

  const getChangeTypeBadge = (type) => {
    const badges = {
      created: 'bg-green-100 text-green-800',
      updated: 'bg-blue-100 text-blue-800',
      permissions_updated: 'bg-purple-100 text-purple-800',
      status_changed: 'bg-yellow-100 text-yellow-800',
      deactivated: 'bg-red-100 text-red-800',
      password_reset: 'bg-orange-100 text-orange-800'
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  if (!staffId) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">
          Activity log will be available after creating the field staff user.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">
          No activity recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Audit Trail:</strong> Complete history of all changes made to this field staff user.
        </p>
      </div>

      <div className="space-y-3">
        {logs.map((log, index) => (
          <div
            key={log.id || index}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChangeTypeBadge(log.change_type)}`}>
                  {getChangeTypeLabel(log.change_type)}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{formatDate(log.changed_at)}</span>
              </div>
            </div>

            {/* Changed By */}
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                Changed by: <strong>{log.changed_by_name || 'Unknown'}</strong>
              </span>
            </div>

            {/* Summary */}
            {log.changes_summary && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded p-2 mt-2">
                {log.changes_summary}
              </div>
            )}

            {/* Before/After Details */}
            {(log.before_snapshot || log.after_snapshot) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <details className="text-xs">
                  <summary className="cursor-pointer text-teal-600 hover:text-teal-700 font-medium">
                    View detailed changes
                  </summary>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    {log.before_snapshot && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Before:</p>
                        <pre className="bg-red-50 text-red-900 p-2 rounded text-xs overflow-auto max-h-32">
                          {JSON.stringify(log.before_snapshot, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.after_snapshot && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">After:</p>
                        <pre className="bg-green-50 text-green-900 p-2 rounded text-xs overflow-auto max-h-32">
                          {JSON.stringify(log.after_snapshot, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityLogTab;
